import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, MoreVertical, Pencil, Trash2, AlertTriangle, Package, Upload, Download, Image, TrendingUp, X, ArrowUpDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAlerts } from '../context/AlertContext';
import { useSettings } from '../context/SettingsContext';
import { useSortableData } from '../hooks/useSortableData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to export CSV with auth token
const exportWithAuth = async (endpoint, filename) => {
  const token = localStorage.getItem('token');
  try {
    const response = await fetch(`${API}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      throw new Error('Export failed');
    }
    const blob = await response.blob();
    
    // Detect Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // For IE/Edge legacy
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename);
      return;
    }
    
    // For Safari - use FileReader to convert to data URL
    if (isSafari) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const link = document.createElement('a');
        link.href = reader.result;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 250);
      };
      reader.readAsDataURL(blob);
      return;
    }
    
    // For Chrome, Firefox, Edge (Chromium)
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 250);
  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
};

const Parts = () => {
  const [searchParams] = useSearchParams();
  const [parts, setParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [showLowStockOnly, setShowLowStockOnly] = useState(searchParams.get('low_stock') === 'true');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [partToDelete, setPartToDelete] = useState(null);
  const [selectedPartForImage, setSelectedPartForImage] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { canManageParts, canViewPrices } = useAuth();
  const { fetchAlerts } = useAlerts();
  const { formatCurrency, getCurrencySymbol } = useSettings();

  const [formData, setFormData] = useState({
    reference_number: '',
    description: '',
    quantity: 0,
    minimum_quantity: 0,
    buying_price: 0,
    selling_price: 0,
    category_id: '',
    location_id: '',
    supplier_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [partsRes, categoriesRes, locationsRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/parts`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/locations`),
        axios.get(`${API}/suppliers`)
      ]);
      setParts(partsRes.data);
      setCategories(categoriesRes.data);
      setLocations(locationsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error('Failed to load parts');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      reference_number: '',
      description: '',
      quantity: 0,
      minimum_quantity: 0,
      buying_price: 0,
      selling_price: 0,
      category_id: '',
      location_id: '',
      supplier_id: ''
    });
    setEditingPart(null);
  };

  const handleOpenDialog = (part = null) => {
    if (part) {
      setEditingPart(part);
      setFormData({
        reference_number: part.reference_number,
        description: part.description,
        quantity: part.quantity,
        minimum_quantity: part.minimum_quantity,
        buying_price: part.buying_price,
        selling_price: part.selling_price,
        category_id: part.category_id,
        location_id: part.location_id,
        supplier_id: part.supplier_id || ''
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        supplier_id: formData.supplier_id === 'none' ? '' : formData.supplier_id
      };
      if (editingPart) {
        await axios.put(`${API}/parts/${editingPart.id}`, submitData);
        toast.success('Part updated successfully');
      } else {
        await axios.post(`${API}/parts`, submitData);
        toast.success('Part created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save part');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/parts/${partToDelete.id}`);
      toast.success('Part deleted successfully');
      setDeleteDialogOpen(false);
      setPartToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete part');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!selectedPartForImage) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/parts/${selectedPartForImage.id}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded successfully');
      setImageDialogOpen(false);
      setSelectedPartForImage(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleImageDelete = async (partId) => {
    try {
      await axios.delete(`${API}/parts/${partId}/image`);
      toast.success('Image deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await axios.post(`${API}/parts/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      if (response.data.success_count > 0) {
        toast.success(`Imported ${response.data.success_count} parts`);
        fetchData();
        fetchAlerts();
      }
      if (response.data.error_count > 0) {
        toast.error(`${response.data.error_count} errors during import`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    window.open(`${API}/parts/import/template`, '_blank');
  };

  // Filter parts by search term, category, location and low stock status
  const searchFilteredParts = parts.filter(part => {
    const matchesSearch = part.reference_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          part.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || part.category_id === filterCategory;
    const matchesLocation = filterLocation === 'all' || part.location_id === filterLocation;
    const matchesLowStock = !showLowStockOnly || part.is_low_stock;
    return matchesSearch && matchesCategory && matchesLocation && matchesLowStock;
  });

  // Apply sorting using the custom hook with reference_number as the name field
  const { sortedData: filteredParts, sortBy, setSortBy, sortOptions } = useSortableData(
    searchFilteredParts, 
    { nameField: 'reference_number' }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="parts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Parts</h1>
          <p className="text-slate-500 mt-1">Manage your inventory parts</p>
        </div>
        {canManageParts() && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                const params = new URLSearchParams();
                if (searchTerm) params.append('search', searchTerm);
                if (filterCategory !== 'all') params.append('category_id', filterCategory);
                if (filterLocation !== 'all') params.append('location_id', filterLocation);
                if (showLowStockOnly) params.append('low_stock_only', 'true');
                try {
                  await exportWithAuth(`/parts/export?${params.toString()}`, 'parts_export.csv');
                  toast.success('Parts exported successfully');
                } catch (error) {
                  toast.error('Failed to export parts');
                }
              }}
              className="rounded-sm"
              data-testid="export-parts-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => { setImportDialogOpen(true); setImportFile(null); setImportResult(null); }}
              className="rounded-sm"
              data-testid="import-parts-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="add-part-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Part
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by reference or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="parts-search-input"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-full md:w-48 rounded-sm" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories
                  .sort((a, b) => (a.path || a.name).localeCompare(b.path || b.name))
                  .map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.level > 0 ? `${'—'.repeat(cat.level)} ${cat.name}` : cat.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Select value={filterLocation} onValueChange={setFilterLocation}>
              <SelectTrigger className="w-full md:w-48 rounded-sm" data-testid="location-filter">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-36 rounded-sm" data-testid="sort-select">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.value.includes('name') ? opt.label : opt.label.split(' ')[0]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={showLowStockOnly ? "destructive" : "outline"}
              onClick={() => setShowLowStockOnly(!showLowStockOnly)}
              className="rounded-sm whitespace-nowrap"
              data-testid="low-stock-filter-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Low Stock
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Parts Table */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-0">
          {filteredParts.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Image</TableHead>
                    <TableHead className="w-32">Reference</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    {canViewPrices() && <TableHead className="text-right">Buy</TableHead>}
                    {canViewPrices() && <TableHead className="text-right">Sell</TableHead>}
                    {canViewPrices() && <TableHead className="text-right">Margin</TableHead>}
                    <TableHead>Status</TableHead>
                    {canManageParts() && <TableHead className="w-12"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredParts.map(part => (
                    <TableRow
                      key={part.id}
                      className={part.is_low_stock ? 'low-stock-row' : ''}
                      data-testid={`part-row-${part.id}`}
                    >
                      <TableCell>
                        {part.image_url ? (
                          <img 
                            src={part.image_url} 
                            alt={part.reference_number}
                            className="w-10 h-10 object-cover rounded-sm border cursor-pointer hover:opacity-80"
                            onClick={() => { setSelectedPartForImage(part); setImageDialogOpen(true); }}
                          />
                        ) : (
                          <div 
                            className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center cursor-pointer hover:bg-slate-200"
                            onClick={() => { setSelectedPartForImage(part); setImageDialogOpen(true); }}
                          >
                            <Image className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono font-semibold text-slate-900">
                        {part.reference_number}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{part.description}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="rounded-sm">
                          {part.category_name}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">{part.location_name}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {part.quantity}
                      </TableCell>
                      {canViewPrices() && (
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(part.buying_price)}
                        </TableCell>
                      )}
                      {canViewPrices() && (
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(part.selling_price)}
                        </TableCell>
                      )}
                      {canViewPrices() && (
                        <TableCell className="text-right">
                          <span className={`font-mono text-sm font-semibold ${
                            part.profit_margin > 20 ? 'text-emerald-600' : 
                            part.profit_margin > 0 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {part.profit_margin != null ? `${part.profit_margin}%` : '-'}
                          </span>
                        </TableCell>
                      )}
                      <TableCell>
                        {part.is_low_stock ? (
                          <Badge variant="destructive" className="rounded-sm">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Low
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 rounded-sm">
                            OK
                          </Badge>
                        )}
                      </TableCell>
                      {canManageParts() && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => { setSelectedPartForImage(part); setImageDialogOpen(true); }}>
                                <Image className="w-4 h-4 mr-2" />
                                {part.image_url ? 'Change Image' : 'Add Image'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleOpenDialog(part)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => {
                                  setPartToDelete(part);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Package className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No parts found</p>
              <p className="text-sm">
                {searchTerm || filterCategory !== 'all' || filterLocation !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Add your first part to get started'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh]" data-testid="part-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">
              {editingPart ? 'Edit Part' : 'Add New Part'}
            </DialogTitle>
            <DialogDescription>
              {editingPart ? 'Update part details' : 'Enter the details for the new part'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="reference_number" className="text-xs sm:text-sm">Reference Number *</Label>
                <Input
                  id="reference_number"
                  value={formData.reference_number}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  required
                  className="rounded-sm font-mono h-9"
                  data-testid="part-reference-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="category_id" className="text-xs sm:text-sm">Category *</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                  required
                >
                  <SelectTrigger className="rounded-sm h-9" data-testid="part-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories
                      .sort((a, b) => (a.path || a.name).localeCompare(b.path || b.name))
                      .map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <span className="flex items-center">
                            {cat.level > 0 && (
                              <span className="text-slate-400 mr-1">{'—'.repeat(cat.level)}</span>
                            )}
                            {cat.name}
                            {cat.level > 0 && (
                              <span className="ml-2 text-xs text-slate-400">({cat.path})</span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-xs sm:text-sm">Description *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                className="rounded-sm h-9"
                data-testid="part-description-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="location_id" className="text-xs sm:text-sm">Location *</Label>
                <Select
                  value={formData.location_id}
                  onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                  required
                >
                  <SelectTrigger className="rounded-sm h-9" data-testid="part-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="supplier_id" className="text-xs sm:text-sm">Supplier</Label>
                <Select
                  value={formData.supplier_id}
                  onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}
                >
                  <SelectTrigger className="rounded-sm h-9" data-testid="part-supplier-select">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {suppliers.map(sup => (
                      <SelectItem key={sup.id} value={sup.id}>{sup.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="quantity" className="text-xs sm:text-sm">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                  className="rounded-sm font-mono h-9"
                  data-testid="part-quantity-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="minimum_quantity" className="text-xs sm:text-sm">Min Qty</Label>
                <Input
                  id="minimum_quantity"
                  type="number"
                  min="0"
                  value={formData.minimum_quantity}
                  onChange={(e) => setFormData({ ...formData, minimum_quantity: parseInt(e.target.value) || 0 })}
                  className="rounded-sm font-mono h-9"
                  data-testid="part-min-quantity-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="buying_price" className="text-xs sm:text-sm">Buy Price ({getCurrencySymbol()})</Label>
                <Input
                  id="buying_price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.buying_price}
                  onChange={(e) => setFormData({ ...formData, buying_price: parseFloat(e.target.value) || 0 })}
                  className="rounded-sm font-mono h-9"
                  data-testid="part-buying-price-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="selling_price" className="text-xs sm:text-sm">Sell Price ({getCurrencySymbol()})</Label>
                <Input
                  id="selling_price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.selling_price}
                  onChange={(e) => setFormData({ ...formData, selling_price: parseFloat(e.target.value) || 0 })}
                  className="rounded-sm font-mono h-9"
                  data-testid="part-selling-price-input"
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm w-full sm:w-auto">
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm w-full sm:w-auto" data-testid="part-submit-btn">
                {editingPart ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-part-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold text-red-600">Delete Part</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete part "{partToDelete?.reference_number}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-sm" data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Upload Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="image-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">Part Image</DialogTitle>
            <DialogDescription>
              {selectedPartForImage?.reference_number} - {selectedPartForImage?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedPartForImage?.image_url && (
              <div className="relative">
                <img 
                  src={selectedPartForImage.image_url} 
                  alt={selectedPartForImage.reference_number}
                  className="w-full h-48 object-contain rounded-sm border"
                />
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2 rounded-sm"
                  onClick={() => handleImageDelete(selectedPartForImage.id)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            <div className="border-2 border-dashed border-slate-200 rounded-sm p-6 text-center">
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
                disabled={uploading}
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600">
                  {uploading ? 'Uploading...' : 'Click to upload image'}
                </p>
                <p className="text-xs text-slate-400 mt-1">JPEG, PNG, GIF, WebP (max 5MB)</p>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">Import Parts from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk import parts into your inventory
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadTemplate} className="rounded-sm flex-1">
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>
            
            <div className="border-2 border-dashed border-slate-200 rounded-sm p-6">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer block text-center">
                <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600">
                  {importFile ? importFile.name : 'Click to select CSV file'}
                </p>
              </label>
            </div>

            {importResult && (
              <div className="space-y-2">
                <div className="flex gap-4">
                  <div className="flex-1 p-3 bg-emerald-50 rounded-sm text-center">
                    <p className="text-2xl font-bold text-emerald-600">{importResult.success_count}</p>
                    <p className="text-xs text-emerald-700">Imported</p>
                  </div>
                  <div className="flex-1 p-3 bg-red-50 rounded-sm text-center">
                    <p className="text-2xl font-bold text-red-600">{importResult.error_count}</p>
                    <p className="text-xs text-red-700">Errors</p>
                  </div>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="max-h-32 overflow-y-auto border rounded-sm p-2 bg-red-50">
                    {importResult.errors.map((err, idx) => (
                      <p key={idx} className="text-xs text-red-600">
                        Row {err.row}: {err.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)} className="rounded-sm">
              Close
            </Button>
            <Button 
              onClick={handleImportCSV} 
              disabled={!importFile || uploading}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="import-submit-btn"
            >
              {uploading ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Parts;
